import React, { useState } from "react";
import { RefreshCw, ChevronDown } from "lucide-react";

interface DashboardHeaderProps {
  period: string;
  onPeriodChange: (period: string) => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  period,
  onPeriodChange,
  onRefresh,
  isRefreshing,
}) => {
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  const periods = [
    { value: "day", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "year", label: "This Year" },
  ];

  const currentPeriod = periods.find((p) => p.value === period);

  const handlePeriodSelect = (value: string) => {
    onPeriodChange(value);
    setShowPeriodDropdown(false);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Dashboard
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          Welcome back! Here's what's happening with your platform.
        </p>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Mobile: Dropdown selector */}
        <div className="relative sm:hidden flex-1">
          <button
            onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700"
          >
            <span>{currentPeriod?.label}</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                showPeriodDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showPeriodDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowPeriodDropdown(false)}
              />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                {periods.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => handlePeriodSelect(p.value)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                      period === p.value
                        ? "bg-gray-100 text-gray-900 font-medium"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Desktop: Button group selector */}
        <div className="hidden sm:flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
          {periods.map((p) => (
            <button
              key={p.value}
              onClick={() => onPeriodChange(p.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                period === p.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Refresh button */}
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          title="Refresh data"
        >
          <RefreshCw
            className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </button>
      </div>
    </div>
  );
};

export default DashboardHeader;
